import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { MentionFilters, ModerationFilters } from '@/lib/api/modules/social-listening'

export const SOCIAL_LISTENING_KEYS = {
  all: ['social-listening'] as const,
  config: ['social-listening', 'config'] as const,
  igStatus: ['social-listening', 'ig-status'] as const,
  mentions: (filters: Record<string, unknown>) =>
    ['social-listening', 'mentions', filters] as const,
  jobs: ['social-listening', 'jobs'] as const,
  moderations: (filters: Record<string, unknown>) =>
    ['social-listening', 'moderations', filters] as const,
}

// ── Journey 1: Instagram OAuth ──────────────────

export function useIgStatus() {
  return useQuery({
    queryKey: SOCIAL_LISTENING_KEYS.igStatus,
    queryFn: () => api.socialListening.getIgStatus(),
  })
}

export function useIgAuthUrl() {
  return useMutation({
    mutationFn: () => api.socialListening.getIgAuthUrl(),
  })
}

export function useDisconnectIg() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.socialListening.disconnectIg(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOCIAL_LISTENING_KEYS.igStatus })
    },
  })
}

export function useSubscribeWebhooks() {
  return useMutation({
    mutationFn: () => api.socialListening.subscribeWebhooks(),
  })
}

export function useResolveUnknownAuthors() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.socialListening.resolveUnknownAuthors(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'mentions'] })
    },
  })
}

export function useEnrichProfiles() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.socialListening.enrichProfiles(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'mentions'] })
    },
  })
}

// ── Journey 2: Config, Sync, Mentions, Jobs ─────

export function useListeningConfig() {
  return useQuery({
    queryKey: SOCIAL_LISTENING_KEYS.config,
    queryFn: () => api.socialListening.getConfig(),
  })
}

export function useUpsertConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      hashtagsToTrack?: string[]
      syncIntervalHours?: number
      isActive?: boolean
      commentModerationEnabled?: boolean
    }) => api.socialListening.upsertConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOCIAL_LISTENING_KEYS.config })
    },
  })
}

export function useMentions(filters: MentionFilters = {}) {
  return useQuery({
    queryKey: SOCIAL_LISTENING_KEYS.mentions(filters),
    queryFn: () => api.socialListening.listMentions(filters),
  })
}

export function useTriggerSync() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.socialListening.triggerSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOCIAL_LISTENING_KEYS.jobs })
      // Invalidate mentions after a short delay to allow queue processing
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['social-listening', 'mentions'] })
      }, 3000)
    },
  })
}

export function useApifySync() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.socialListening.apifySync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SOCIAL_LISTENING_KEYS.jobs })
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'mentions'] })
    },
  })
}

export function useSyncJobs() {
  return useQuery({
    queryKey: SOCIAL_LISTENING_KEYS.jobs,
    queryFn: () => api.socialListening.listJobs(),
  })
}

// ── Journey 4: Status management & creator linking ─────

export function useUpdateMentionStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string
      status: 'new' | 'reviewed' | 'linked' | 'ignored'
    }) => api.socialListening.updateMentionStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'mentions'] })
    },
  })
}

export function useLinkMentionToCreator() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, creatorId }: { id: string; creatorId: string }) =>
      api.socialListening.linkMentionToCreator(id, creatorId),
    onSuccess: (_data, { creatorId }) => {
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'mentions'] })
      queryClient.invalidateQueries({ queryKey: ['creators', creatorId, 'posts'] })
    },
  })
}

// ── Comment Moderation ──────────────────────────

export function useModerations(filters: ModerationFilters = {}) {
  return useQuery({
    queryKey: SOCIAL_LISTENING_KEYS.moderations(filters),
    queryFn: () => api.socialListening.listModerations(filters),
  })
}

export function useHideComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.socialListening.hideComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'moderations'] })
    },
  })
}

export function useUnhideComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.socialListening.unhideComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'moderations'] })
    },
  })
}

export function useReplyToComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { message: string; unhide?: boolean } }) =>
      api.socialListening.replyToComment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-listening', 'moderations'] })
    },
  })
}

export function useSuggestReply() {
  return useMutation({
    mutationFn: (id: string) => api.socialListening.suggestReply(id),
  })
}
